import { useEffect, useState } from "react";

/**
 * Reactive `navigator.onLine` wrapper.
 *
 * `navigator.onLine` is famously unreliable on some platforms (it only
 * tells you whether the OS thinks you have a network interface, not
 * whether the internet is actually reachable), but it's the best signal
 * we have without constant health-check pings. The offline-queue path
 * also detects failures at request time, so this hook is only used for
 * UI affordances — the banner, the "queued offline" badge — not for
 * gating real business logic.
 */
export function useOnlineStatus(): boolean {
  const [online, setOnline] = useState<boolean>(
    typeof navigator === "undefined" ? true : navigator.onLine,
  );

  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => {
      window.removeEventListener("online", up);
      window.removeEventListener("offline", down);
    };
  }, []);

  return online;
}
